import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Bloco3D, COR as COR_BASE, FONTE, Pilula, sxCard } from '../../pages/OxetechAcademy/ui';

// Kit visual da area do laboratorio (mockups de 07/10/2026): fundo creme,
// cartoes brancos com borda creme, titulos Poppins e o quadrado de icone (Bloco3D) da Academy.
// Usado no painel da turma, calendario, catalogo, diarios, sala de controle e
// solicitacoes — mexer aqui muda todas essas telas.
export { Bloco3D, FONTE, Pilula, sxCard };

export const COR = {
  ...COR_BASE,
  fundo: '#F8F5EF',
  primaria: '#C4561F',
  primariaHover: '#A8450F',
  primariaClara: '#FBEDE4',
};

// Botao laranja cheio (acao principal da secao).
export const sxBotaoPrimario = {
  bgcolor: COR.primaria,
  color: '#fff',
  fontFamily: FONTE,
  fontWeight: 600,
  fontSize: 15,
  textTransform: 'none',
  borderRadius: '12px',
  px: '22px',
  py: '10px',
  boxShadow: '0 8px 18px rgba(196,86,31,.25)',
  whiteSpace: 'nowrap',
  '&:hover': { bgcolor: COR.primariaHover, boxShadow: '0 8px 18px rgba(196,86,31,.32)' },
  '&.Mui-disabled': { bgcolor: '#EEE8DF', color: '#B5AC9F', boxShadow: 'none' },
};

// Botao contornado; `cor` muda borda/texto (laranja por padrao).
export const sxBotaoContorno = (cor = COR.primaria) => ({
  color: cor,
  bgcolor: '#fff',
  border: `1.5px solid ${cor}40`,
  fontFamily: FONTE,
  fontWeight: 600,
  fontSize: 15,
  textTransform: 'none',
  borderRadius: '12px',
  px: '20px',
  py: '9px',
  whiteSpace: 'nowrap',
  '&:hover': { bgcolor: `${cor}0D`, border: `1.5px solid ${cor}80` },
});

// Cabecalho de pagina: rotulo "OXETECH LAB", quadrado de icone grande, titulo e subtitulo.
export function CabecalhoPagina({ cor = 'laranja', icone, rotulo = 'OxeTech Lab', titulo, subtitulo, acao }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '16px 20px', mb: '28px', fontFamily: FONTE }}>
      <Box sx={{ mt: '22px', display: { xs: 'none', sm: 'block' } }}>
        <Bloco3D cor={cor} tamanho={64} icone={icone} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 220 }}>
        {rotulo && (
          <Typography sx={{ fontFamily: FONTE, fontSize: 13, fontWeight: 600, letterSpacing: '.22em', textTransform: 'uppercase', color: COR.primaria, mb: '4px' }}>
            {rotulo}
          </Typography>
        )}
        <Typography component="h1" sx={{ fontFamily: FONTE, fontSize: { xs: 26, md: 34 }, fontWeight: 700, letterSpacing: '-.02em', color: COR.tinta, lineHeight: 1.15 }}>
          {titulo}
        </Typography>
        {subtitulo && <Typography sx={{ fontFamily: FONTE, fontSize: 15.5, color: COR.suave, mt: '8px' }}>{subtitulo}</Typography>}
      </Box>
      {acao}
    </Box>
  );
}

// Titulo de secao/aba: quadrado de icone pequeno + titulo (+ subtitulo) e acoes a direita.
export function TituloSecao({ cor = 'laranja', icone, titulo, subtitulo, contador, acoes, sx }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: subtitulo ? 'flex-start' : 'center', justifyContent: 'space-between', gap: '12px 16px', mb: '18px', fontFamily: FONTE, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: subtitulo ? 'flex-start' : 'center', gap: '14px', minWidth: 0, flex: '1 1 260px' }}>
        <Bloco3D cor={cor} tamanho={42} icone={icone} />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Typography component="h2" sx={{ fontFamily: FONTE, fontSize: { xs: 20, md: 23 }, fontWeight: 700, letterSpacing: '-.015em', color: COR.tinta, lineHeight: 1.25 }}>
              {titulo}
            </Typography>
            {contador != null && (
              <Pilula cor={COR.vermelho} fundo={COR.vermelhoClaro} sx={{ fontWeight: 700 }}>{contador}</Pilula>
            )}
          </Box>
          {subtitulo && <Typography sx={{ fontFamily: FONTE, fontSize: 14.5, color: COR.apagado, mt: '2px', maxWidth: 760 }}>{subtitulo}</Typography>}
        </Box>
      </Box>
      {acoes && <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>{acoes}</Box>}
    </Box>
  );
}

// Estado vazio: cartao tracejado com o quadrado de icone e o texto. (O bloco
// flutuando com sombra no chao e pontinhos piscando saiu em 15/10/2026, junto
// com o visual 3D.)
export function EstadoVazio({ cor = 'laranja', icone, titulo, texto, acao, sx }) {
  return (
    <Box
      sx={{
        width: '100%', boxSizing: 'border-box', bgcolor: '#fff', border: `1.5px dashed ${COR.borda}`, borderRadius: '20px',
        p: { xs: '40px 20px', md: '52px 32px' }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', fontFamily: FONTE,
        ...sx,
      }}
    >
      <Box sx={{ mb: '18px' }}>
        <Bloco3D cor={cor} tamanho={64} icone={icone} />
      </Box>
      <Typography sx={{ fontFamily: FONTE, fontSize: 18, fontWeight: 700, color: COR.tinta }}>{titulo}</Typography>
      {texto && <Typography sx={{ fontFamily: FONTE, fontSize: 15, color: COR.apagado, mt: '8px', maxWidth: 520, lineHeight: 1.6 }}>{texto}</Typography>}
      {acao && <Box sx={{ mt: '22px' }}>{acao}</Box>}
    </Box>
  );
}

// Card de numero (dashboard): quadrado de icone, valor grande na cor e rotulo.
export function CardNumero({ cor = 'laranja', icone, valor, rotulo, corValor, horizontal = false }) {
  const tom = corValor || { laranja: COR.laranja, verde: COR.verde, azul: COR.azul, dourado: '#B8862A', roxo: COR.roxo, rosa: '#C9405F' }[cor] || COR.tinta;
  if (horizontal) {
    return (
      <Box sx={{ ...sxCard, p: '22px 24px', display: 'flex', alignItems: 'center', gap: '18px', height: '100%' }}>
        <Bloco3D cor={cor} tamanho={52} icone={icone} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONTE, fontSize: 14.5, color: COR.suave }}>{rotulo}</Typography>
          <Typography sx={{ fontFamily: FONTE, fontSize: 28, fontWeight: 700, color: COR.tinta, lineHeight: 1.2 }}>{valor}</Typography>
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ ...sxCard, p: '26px 16px 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', height: '100%' }}>
      <Bloco3D cor={cor} tamanho={54} icone={icone} />
      <Typography sx={{ fontFamily: FONTE, fontSize: 34, fontWeight: 700, color: tom, lineHeight: 1.1, mt: '14px' }}>{valor}</Typography>
      <Typography sx={{ fontFamily: FONTE, fontSize: 15, color: COR.suave, mt: '6px' }}>{rotulo}</Typography>
    </Box>
  );
}

// Iniciais em circulo (avatar dos alunos).
export function Inicial({ nome, cor = COR.primaria, fundo, tamanho = 40, solido = false }) {
  const letra = String(nome || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <Box
      aria-hidden
      sx={{
        width: tamanho, height: tamanho, flex: 'none', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FONTE, fontWeight: 700, fontSize: Math.round(tamanho * 0.4),
        ...(solido
          ? { color: '#fff', background: `linear-gradient(155deg, ${cor}CC, ${cor})`, boxShadow: `inset 0 -3px 0 rgba(0,0,0,.15), 0 6px 12px ${cor}40` }
          : { color: cor, bgcolor: fundo || `${cor}14` }),
      }}
    >
      {letra}
    </Box>
  );
}

export function BotaoPrimario(props) {
  const { sx, ...resto } = props;
  return <Button variant="contained" disableElevation {...resto} sx={{ ...sxBotaoPrimario, ...sx }} />;
}

export function BotaoContorno({ cor, sx, ...resto }) {
  return <Button variant="outlined" {...resto} sx={{ ...sxBotaoContorno(cor), ...sx }} />;
}
